import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import InputMask from 'react-input-mask';
import {
  Calendar,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@cargomatic/ui';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { getClassName } from '../../utils/ui/classes';

const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY';

// Build a react-input-mask array mask from a moment-style format string
// (`MM/DD/YYYY`, `DD/MM/YYYY`, `YYYY-MM-DD`, ...): runs of M/D/Y become digit
// slots and any other character is preserved as a literal separator.
function getMaskForFormat(dateFormat) {
  const fmt = dateFormat || DEFAULT_DATE_FORMAT;
  const mask = [];
  for (let i = 0; i < fmt.length; i += 1) {
    const ch = fmt[i];
    if (ch === 'M' || ch === 'D' || ch === 'Y') {
      mask.push(/[0-9]/);
    } else {
      mask.push(ch);
    }
  }
  return mask;
}

function hasDigits(text) {
  return typeof text === 'string' && /\d/.test(text);
}

// Accept a wide range of typed inputs so US, European, ISO, and dash-separated
// dates all parse without forcing the user to match the display format.
const FALLBACK_PARSE_FORMATS = [
  'MM/DD/YYYY',
  'M/D/YYYY',
  'M/D/YY',
  'MM/DD/YY',
  'DD/MM/YYYY',
  'D/M/YYYY',
  'YYYY-MM-DD',
  'YYYY/MM/DD',
  'MM-DD-YYYY',
  'M-D-YYYY',
  'DD-MM-YYYY',
];

function getParseFormats(dateFormat) {
  const ordered = [dateFormat, ...FALLBACK_PARSE_FORMATS];
  return Array.from(new Set(ordered));
}

function toDate(value) {
  if (!value) return undefined;
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? undefined : value;
  if (moment.isMoment(value))
    return value.isValid() ? value.toDate() : undefined;
  const m = moment(value);
  return m.isValid() ? m.toDate() : undefined;
}

function formatValue(value, dateFormat) {
  const d = toDate(value);
  return d ? moment(d).format(dateFormat) : '';
}

/**
 * Drop-in-shaped replacement for the react-datepicker-based v2/DatePicker.
 * Same API: `selected`, `onChange(Date|null)`, `onChangeRaw(event)`,
 * `placeholderText`, `isClearable`, `className`. Built on @cargomatic/ui
 * primitives (Input, Calendar, Popover) with react-input-mask supplying the
 * canonical `__/__/____` slash mask while typing.
 */
const CargomaticDatePicker = ({
  selected,
  onChange,
  onChangeRaw,
  placeholderText,
  isClearable,
  className,
  dataTestId,
  dateFormat = DEFAULT_DATE_FORMAT,
  alwaysShowMask = true,
}) => {
  const parseFormats = useMemo(() => getParseFormats(dateFormat), [dateFormat]);
  const mask = useMemo(() => getMaskForFormat(dateFormat), [dateFormat]);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(() => formatValue(selected, dateFormat));
  const [calendarMonth, setCalendarMonth] = useState(
    () => toDate(selected) || new Date(),
  );
  const isUserEditing = useRef(false);

  useEffect(() => {
    if (isUserEditing.current) return;
    setText(formatValue(selected, dateFormat));
  }, [selected, dateFormat]);

  useEffect(() => {
    const d = toDate(selected);
    if (d) setCalendarMonth(d);
  }, [selected]);

  const handleTextChange = (e) => {
    const value = e.target.value;
    isUserEditing.current = true;
    setText(value);
    onChangeRaw?.(e);
    // react-input-mask emits values like "1_/__/____" while the user is still
    // typing; treat anything without digits as empty, and only attempt to
    // parse once no mask placeholder underscores remain.
    if (!hasDigits(value)) {
      onChange?.(null);
      return;
    }
    if (value.includes('_')) return;
    const m = moment(value, parseFormats, true);
    if (m.isValid()) {
      onChange?.(m.toDate());
    }
  };

  const handleBlur = () => {
    isUserEditing.current = false;
    // Keep the typed text only when it fully parses to a valid date; otherwise
    // reset to the selected value (empty string when nothing is selected) so
    // partial masks (`12/__/____`) and invalid dates don't linger after blur.
    if (hasDigits(text) && !text.includes('_')) {
      const m = moment(text, parseFormats, true);
      if (m.isValid()) {
        setText(m.format(dateFormat));
        return;
      }
    }
    setText(formatValue(selected, dateFormat));
  };

  const handleCalendarSelect = (date) => {
    if (!date) {
      setText('');
      onChange?.(null);
    } else {
      setText(moment(date).format(dateFormat));
      onChange?.(date);
    }
    setOpen(false);
  };

  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setText('');
    onChange?.(null);
  };

  const selectedDate = toDate(selected);

  const containerClass = getClassName(
    {
      relative: true,
      'is-invalid':
        typeof className === 'object' && className && className.error,
    },
    typeof className === 'string' ? className : null,
  );

  return (
    <div className={containerClass} data-testid={dataTestId}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground z-10"
            aria-label="Open calendar"
            tabIndex={-1}
          >
            <CalendarIcon className="size-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <Calendar
            mode="single"
            selected={selectedDate}
            month={calendarMonth}
            onMonthChange={setCalendarMonth}
            onSelect={handleCalendarSelect}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      <InputMask
        mask={mask}
        value={text}
        onChange={handleTextChange}
        onFocus={() => {
          isUserEditing.current = true;
        }}
        onBlur={handleBlur}
        alwaysShowMask={alwaysShowMask}
      >
        <Input
          type="text"
          placeholder={placeholderText || dateFormat}
          className="pl-9 pr-8"
        />
      </InputMask>
      {isClearable && hasDigits(text) && (
        <button
          type="button"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground z-10"
          aria-label="Clear date"
          onClick={handleClear}
          tabIndex={-1}
        >
          <X className="size-4" />
        </button>
      )}
    </div>
  );
};

CargomaticDatePicker.propTypes = {
  selected: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.object, // moment
    PropTypes.string,
  ]),
  onChange: PropTypes.func,
  onChangeRaw: PropTypes.func,
  placeholderText: PropTypes.string,
  isClearable: PropTypes.bool,
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  dataTestId: PropTypes.string,
  dateFormat: PropTypes.string,
  alwaysShowMask: PropTypes.bool,
};

export default CargomaticDatePicker;
